import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import InstallPrompt from "@/components/InstallPrompt";
import WhatsNewDrawer from "@/components/WhatsNewDrawer";
import RecommendedNext from "@/components/RecommendedNext";
import PageviewTracker from "@/components/PageviewTracker";
import DashboardPage from "@/pages/DashboardPage";
import LoginPage from "@/pages/LoginPage";
import HistoryPage from "@/pages/HistoryPage";
import ToolsPage from "@/pages/ToolsPage";
import GearPage from "@/pages/GearPage";
import ProjectsPage from "@/pages/ProjectsPage";
import CareersPage from "@/pages/CareersPage";
import GuidePage from "@/pages/GuidePage";
import PresenterPage from "@/pages/PresenterPage";
import LightingPage from "@/pages/LightingPage";
import GoldenHourPage from "@/pages/GoldenHourPage";
import GlossaryPage from "@/pages/GlossaryPage";
import QuizzesPage from "@/pages/QuizzesPage";
import ExamPage from "@/pages/ExamPage";
import CertificatePage from "@/pages/CertificatePage";
import TeacherProgressPage from "@/pages/TeacherProgressPage";
import HallOfFamePage from "@/pages/HallOfFamePage";
import { QuizBadgesProvider } from "@/contexts/QuizBadgesContext";
import LessonQuizSection from "@/components/quiz/LessonQuizSection";
import StudioPage from "@/pages/StudioPage";
import PrintingPage from "@/pages/PrintingPage";
import ShotsPage from "@/pages/ShotsPage";
import CalibrationPage from "@/pages/CalibrationPage";
import TetherPage from "@/pages/TetherPage";
import HistogramPage from "@/pages/HistogramPage";
import MeteringModesPage from "@/pages/MeteringModesPage";
import ExposureCompensationPage from "@/pages/ExposureCompensationPage";
import CropFactorPage from "@/pages/CropFactorPage";
import FiltersPage from "@/pages/FiltersPage";
import LensCompressionPage from "@/pages/LensCompressionPage";
import AdvancedFocusPage from "@/pages/AdvancedFocusPage";
import MaintenancePage from "@/pages/MaintenancePage";
import WhiteBalancePage from "@/pages/WhiteBalancePage";
import FormsPage from "@/pages/FormsPage";
import GrayCardPage from "@/pages/GrayCardPage";
import SmartphonePhotographyPage from "@/pages/SmartphonePhotographyPage";
import FilmPhotographyPage from "@/pages/FilmPhotographyPage";
import BlackWhitePage from "@/pages/BlackWhitePage";
import PrepChecklistPage from "@/pages/PrepChecklistPage";
import PosesPage from "@/pages/PosesPage";
import ModeDialPage from "@/pages/ModeDialPage";
import LightMeterPage from "@/pages/LightMeterPage";
import LandingPage from "@/pages/LandingPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import QRPosterPage from "@/pages/QRPosterPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AnatomyPage from "@/pages/AnatomyPage";
import BayerFilterPage from "@/pages/BayerFilterPage";
import CameraHistoryPage from "@/pages/CameraHistoryPage";
import LightingBasicsPage from "@/pages/LightingBasicsPage";
import TaskCardsPage from "@/pages/TaskCardsPage";
import "@/App.css";

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === null) return null; // still loading
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route
        path="/"
        element={
          user && user !== false ? (
            <DashboardPage />
          ) : user === false ? (
            <LandingPage />
          ) : null
        }
      />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/qr-poster" element={<QRPosterPage />} />

      {/* All product/learning pages require an account. */}
      <Route path="/tools" element={<RequireAuth><ToolsPage /></RequireAuth>} />
      <Route path="/projects" element={<RequireAuth><ProjectsPage /></RequireAuth>} />
      <Route path="/careers" element={<RequireAuth><CareersPage /></RequireAuth>} />
      <Route path="/lighting" element={<RequireAuth><LightingPage /></RequireAuth>} />
      <Route path="/golden-hour" element={<RequireAuth><GoldenHourPage /></RequireAuth>} />
      <Route path="/glossary" element={<RequireAuth><GlossaryPage /></RequireAuth>} />
      <Route path="/quizzes" element={<RequireAuth><QuizzesPage /></RequireAuth>} />
      <Route path="/exam" element={<RequireAuth><ExamPage /></RequireAuth>} />
      <Route path="/certificate/:id" element={<CertificatePage />} />
      <Route path="/teacher-progress" element={<RequireAuth><TeacherProgressPage /></RequireAuth>} />
      <Route path="/hall-of-fame" element={<RequireAuth><HallOfFamePage /></RequireAuth>} />
      <Route path="/studio" element={<RequireAuth><StudioPage /></RequireAuth>} />
      <Route path="/printing" element={<RequireAuth><PrintingPage /></RequireAuth>} />
      <Route path="/shots" element={<RequireAuth><ShotsPage /></RequireAuth>} />
      <Route path="/calibration" element={<RequireAuth><CalibrationPage /></RequireAuth>} />
      <Route path="/tether" element={<RequireAuth><TetherPage /></RequireAuth>} />
      <Route path="/histogram" element={<RequireAuth><HistogramPage /></RequireAuth>} />
      <Route path="/metering" element={<RequireAuth><MeteringModesPage /></RequireAuth>} />
      <Route path="/exposure-compensation" element={<RequireAuth><ExposureCompensationPage /></RequireAuth>} />
      <Route path="/crop-factor" element={<RequireAuth><CropFactorPage /></RequireAuth>} />
      <Route path="/filters" element={<RequireAuth><FiltersPage /></RequireAuth>} />
      <Route path="/lens-compression" element={<RequireAuth><LensCompressionPage /></RequireAuth>} />
      <Route path="/advanced-focus" element={<RequireAuth><AdvancedFocusPage /></RequireAuth>} />
      <Route path="/maintenance" element={<RequireAuth><MaintenancePage /></RequireAuth>} />
      <Route path="/white-balance" element={<RequireAuth><WhiteBalancePage /></RequireAuth>} />
      <Route path="/anatomy" element={<RequireAuth><AnatomyPage /></RequireAuth>} />
      <Route path="/bayer-filter" element={<RequireAuth><BayerFilterPage /></RequireAuth>} />
      <Route path="/camera-history" element={<RequireAuth><CameraHistoryPage /></RequireAuth>} />
      <Route path="/lighting-basics" element={<RequireAuth><LightingBasicsPage /></RequireAuth>} />
      <Route path="/task-cards" element={<RequireAuth><TaskCardsPage /></RequireAuth>} />
      <Route path="/forms" element={<RequireAuth><FormsPage /></RequireAuth>} />
      <Route path="/gray-card" element={<RequireAuth><GrayCardPage /></RequireAuth>} />
      <Route path="/smartphone-photography" element={<RequireAuth><SmartphonePhotographyPage /></RequireAuth>} />
      <Route path="/film-photography" element={<RequireAuth><FilmPhotographyPage /></RequireAuth>} />
      <Route path="/black-white" element={<RequireAuth><BlackWhitePage /></RequireAuth>} />
      <Route path="/prep-checklist" element={<RequireAuth><PrepChecklistPage /></RequireAuth>} />
      <Route path="/poses" element={<RequireAuth><PosesPage /></RequireAuth>} />
      <Route path="/mode-dial" element={<RequireAuth><ModeDialPage /></RequireAuth>} />
      <Route path="/light-meter" element={<RequireAuth><LightMeterPage /></RequireAuth>} />
      <Route path="/guide" element={<RequireAuth><GuidePage /></RequireAuth>} />
      <Route path="/presenter" element={<RequireAuth><PresenterPage /></RequireAuth>} />
      <Route path="/history" element={<RequireAuth><HistoryPage /></RequireAuth>} />
      <Route path="/gear" element={<RequireAuth><GearPage /></RequireAuth>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <QuizBadgesProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-[#0A0A0A]">
            <Navbar />
            <main>
              <AppRoutes />
              <LessonQuizSection />
              <RecommendedNext />
            </main>
          </div>
          <PageviewTracker />
          <Toaster theme="dark" position="top-right" richColors />
          <InstallPrompt />
          <WhatsNewDrawer />
        </BrowserRouter>
        </QuizBadgesProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
